import { useState } from 'react'
import { coaRequestService } from '../services/coaRequestService'
import './RequestCOAPage.css'

interface SampleInfo {
  productName: string
  lotNumber: string
  quantity: string
  purityAndId: boolean // $1200 (required)
  netContent: boolean // $20
  endotoxins: boolean // $180
  sterility: boolean // $100
  conformity: boolean // $20/vial
  heavyMetals: boolean // $120
  fentanyl: boolean // $20
  publishToPublic: boolean // $70/vial
}

type Step = 'info' | 'samples' | 'shipping' | 'review'

function RequestCOAPage() {
  const [currentStep, setCurrentStep] = useState<Step>('info')
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
  })

  const [samples, setSamples] = useState<SampleInfo[]>([
    { productName: '', lotNumber: '', quantity: '', purityAndId: true, netContent: false, endotoxins: false, sterility: false, conformity: false, heavyMetals: false, fentanyl: false, publishToPublic: false },
  ])

  const [shippingData, setShippingData] = useState({
    shippingAddress: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    specialInstructions: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const steps: { key: Step; label: string }[] = [
    { key: 'info', label: 'Contact Info' },
    { key: 'samples', label: 'Samples' },
    { key: 'shipping', label: 'Shipping' },
    { key: 'review', label: 'Review & Pay' },
  ]

  const calculatePrice = () => {
    let total = 0
    samples.forEach((sample) => {
      // Base test - Purity & ID (required)
      total += 120
      
      // Optional tests
      if (sample.netContent) total += 20
      if (sample.endotoxins) total += 180
      if (sample.sterility) total += 100
      if (sample.conformity) total += 20 * parseInt(sample.quantity || '1')
      if (sample.heavyMetals) total += 120
      if (sample.fentanyl) total += 20
      if (sample.publishToPublic) total += 70 * parseInt(sample.quantity || '1')
    })
    return total
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleShippingChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setShippingData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSampleChange = (index: number, field: keyof SampleInfo, value: string | boolean) => {
    const newSamples = [...samples]
    newSamples[index] = { ...newSamples[index], [field]: value }
    setSamples(newSamples)
  }

  const addSample = () => {
    setSamples([...samples, { productName: '', lotNumber: '', quantity: '', purityAndId: true, netContent: false, endotoxins: false, sterility: false, conformity: false, heavyMetals: false, fentanyl: false, publishToPublic: false }])
  }

  const removeSample = (index: number) => {
    if (samples.length > 1) {
      setSamples(samples.filter((_, i) => i !== index))
    }
  }

  const nextStep = () => {
    const stepOrder: Step[] = ['info', 'samples', 'shipping', 'review']
    const currentIndex = stepOrder.indexOf(currentStep)
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1])
    }
  }

  const prevStep = () => {
    const stepOrder: Step[] = ['info', 'samples', 'shipping', 'review']
    const currentIndex = stepOrder.indexOf(currentStep)
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      await coaRequestService.submitCOARequest({
        companyName: formData.companyName,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone || undefined,
        samples: samples.map((s) => ({
          productName: s.productName,
          lotNumber: s.lotNumber,
          quantity: parseInt(s.quantity || '1'),
          tests: {
            purityAndId: s.purityAndId,
            netContent: s.netContent,
            endotoxins: s.endotoxins,
            sterility: s.sterility,
            conformity: s.conformity,
            heavyMetals: s.heavyMetals,
            fentanyl: s.fentanyl,
            publishToPublic: s.publishToPublic,
          },
        })),
        shipping: {
          streetAddress: shippingData.shippingAddress,
          city: shippingData.city,
          state: shippingData.state,
          country: shippingData.country,
          postalCode: shippingData.zipCode,
          specialInstructions: shippingData.specialInstructions || undefined,
        },
      })

      setSubmitSuccess(true)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const canProceed = () => {
    switch (currentStep) {
      case 'info':
        return formData.companyName && formData.contactName && formData.email
      case 'samples':
        return samples.every(s => s.productName && s.lotNumber && s.quantity)
      case 'shipping':
        return shippingData.shippingAddress && shippingData.city && shippingData.state && shippingData.country && shippingData.zipCode
      case 'review':
        return true
      default:
        return false
    }
  }

  if (submitSuccess) {
    return (
      <div className="request-coa-page">
        <div className="success-container">
          <div className="success-icon">✓</div>
          <h2>Submission Received</h2>
          <p>
            Thank you for your submission. An invoice and instructions for sending your
            samples will be sent to your email address shortly.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => {
              setSubmitSuccess(false)
              setCurrentStep('info')
              setFormData({
                companyName: '',
                contactName: '',
                email: '',
                phone: '',
              })
              setSamples([{ productName: '', lotNumber: '', quantity: '', purityAndId: true, netContent: false, endotoxins: false, sterility: false, conformity: false, heavyMetals: false, fentanyl: false, publishToPublic: false }])
              setShippingData({
                shippingAddress: '',
                city: '',
                state: '',
                country: '',
                zipCode: '',
                specialInstructions: '',
              })
            }}
          >
            Submit Another Request
          </button>
        </div>
      </div>
    )
  }

  const currentStepIndex = steps.findIndex(s => s.key === currentStep)

  return (
    <div className="request-coa-page">
      <div className="page-header">
        <h1>Submit Samples for Testing</h1>
        <p className="subtitle">
          Complete the form to request Certificate of Analysis testing for your peptide samples
        </p>
      </div>

      {/* Progress Steps */}
      <div className="progress-steps">
        {steps.map((step, index) => (
          <div key={step.key} className={`step ${index <= currentStepIndex ? 'active' : ''} ${index < currentStepIndex ? 'completed' : ''}`}>
            <div className="step-number">{index < currentStepIndex ? '✓' : index + 1}</div>
            <div className="step-label">{step.label}</div>
          </div>
        ))}
      </div>

      <form className="coa-form" onSubmit={handleSubmit}>
        {/* Step 1: Contact Info */}
        {currentStep === 'info' && (
          <section className="form-section">
            <h2>Company Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="companyName">Company / Vendor Name *</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  placeholder="Your company name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contactName">Contact Name *</label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  required
                  placeholder="Your full name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="email@company.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 555-5555"
                />
              </div>
            </div>
          </section>
        )}

        {/* Step 2: Samples */}
        {currentStep === 'samples' && (
          <section className="form-section">
            <h2>Sample Details</h2>
            <p className="section-description">
              Enter the products you want to test. Select the tests you need for each sample.
            </p>
            {samples.map((sample, index) => (
              <div key={index} className="sample-row">
                <div className="sample-header">
                  <span className="sample-number">Sample {index + 1}</span>
                  {samples.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove-sample"
                      onClick={() => removeSample(index)}
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="sample-fields">
                  <div className="form-group">
                    <label>Product Name *</label>
                    <input
                      type="text"
                      value={sample.productName}
                      onChange={(e) => handleSampleChange(index, 'productName', e.target.value)}
                      required
                      placeholder="e.g., BPC-157, GLP-1, Tirzepatide"
                    />
                  </div>
                  <div className="form-group">
                    <label>Lot Number *</label>
                    <input
                      type="text"
                      value={sample.lotNumber}
                      onChange={(e) => handleSampleChange(index, 'lotNumber', e.target.value)}
                      required
                      placeholder="e.g., LOT20240115"
                    />
                  </div>
                  <div className="form-group">
                    <label>Quantity (vials) *</label>
                    <input
                      type="number"
                      min="1"
                      value={sample.quantity}
                      onChange={(e) => handleSampleChange(index, 'quantity', e.target.value)}
                      required
                      placeholder="Number of vials"
                    />
                  </div>
                </div>
                
                <div className="test-options">
                  <h4>Select Tests for This Sample</h4>
                  <div className="test-options-grid">
                    <label className="test-option required">
                      <input
                        type="checkbox"
                        checked={sample.purityAndId}
                        onChange={(e) => handleSampleChange(index, 'purityAndId', e.target.checked)}
                      />
                      <span className="test-option-content">
                        <span className="test-name">Purity & ID</span>
                        <span className="test-price">$120</span>
                      </span>
                    </label>
                    <label className="test-option">
                      <input
                        type="checkbox"
                        checked={sample.netContent}
                        onChange={(e) => handleSampleChange(index, 'netContent', e.target.checked)}
                      />
                      <span className="test-option-content">
                        <span className="test-name">Net Content</span>
                        <span className="test-price">$20</span>
                      </span>
                    </label>
                    <label className="test-option">
                      <input
                        type="checkbox"
                        checked={sample.endotoxins}
                        onChange={(e) => handleSampleChange(index, 'endotoxins', e.target.checked)}
                      />
                      <span className="test-option-content">
                        <span className="test-name">Endotoxins¹</span>
                        <span className="test-price">$180</span>
                      </span>
                    </label>
                    <label className="test-option">
                      <input
                        type="checkbox"
                        checked={sample.sterility}
                        onChange={(e) => handleSampleChange(index, 'sterility', e.target.checked)}
                      />
                      <span className="test-option-content">
                        <span className="test-name">Sterility¹</span>
                        <span className="test-price">$100</span>
                      </span>
                    </label>
                    <label className="test-option">
                      <input
                        type="checkbox"
                        checked={sample.conformity}
                        onChange={(e) => handleSampleChange(index, 'conformity', e.target.checked)}
                      />
                      <span className="test-option-content">
                        <span className="test-name">Conformity</span>
                        <span className="test-price">$20/vial</span>
                      </span>
                    </label>
                    <label className="test-option">
                      <input
                        type="checkbox"
                        checked={sample.heavyMetals}
                        onChange={(e) => handleSampleChange(index, 'heavyMetals', e.target.checked)}
                      />
                      <span className="test-option-content">
                        <span className="test-name">Heavy Metals</span>
                        <span className="test-price">$120</span>
                      </span>
                    </label>
                    <label className="test-option">
                      <input
                        type="checkbox"
                        checked={sample.fentanyl}
                        onChange={(e) => handleSampleChange(index, 'fentanyl', e.target.checked)}
                      />
                      <span className="test-option-content">
                        <span className="test-name">Fentanyl</span>
                        <span className="test-price">$20</span>
                      </span>
                    </label>
                    <label className="test-option">
                      <input
                        type="checkbox"
                        checked={sample.publishToPublic}
                        onChange={(e) => handleSampleChange(index, 'publishToPublic', e.target.checked)}
                      />
                      <span className="test-option-content">
                        <span className="test-name">Publish to Public DB</span>
                        <span className="test-price">$70/vial</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-secondary" onClick={addSample}>
              + Add Another Sample
            </button>
          </section>
        )}

        {/* Step 3: Shipping */}
        {currentStep === 'shipping' && (
          <section className="form-section">
            <h2>Shipping Address</h2>
            <p className="section-description">
              Ship to: Unit 12, Skyport Dr, West Drayton UB7 0LB, United Kingdom
            </p>
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="shippingAddress">Street Address *</label>
                <input
                  type="text"
                  id="shippingAddress"
                  name="shippingAddress"
                  value={shippingData.shippingAddress}
                  onChange={handleShippingChange}
                  required
                  placeholder="123 Business Park Dr"
                />
              </div>
              <div className="form-group">
                <label htmlFor="city">City *</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={shippingData.city}
                  onChange={handleShippingChange}
                  required
                  placeholder="City"
                />
              </div>
              <div className="form-group">
                <label htmlFor="state">State / Province / Region *</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={shippingData.state}
                  onChange={handleShippingChange}
                  required
                  placeholder="State or region"
                />
              </div>
              <div className="form-group">
                <label htmlFor="country">Country *</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={shippingData.country}
                  onChange={handleShippingChange}
                  required
                  placeholder="Country"
                />
              </div>
              <div className="form-group">
                <label htmlFor="zipCode">Postal Code *</label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={shippingData.zipCode}
                  onChange={handleShippingChange}
                  required
                  placeholder="Postal code"
                />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '20px' }}>
              <label htmlFor="specialInstructions">Special Instructions</label>
              <textarea
                id="specialInstructions"
                name="specialInstructions"
                value={shippingData.specialInstructions}
                onChange={handleShippingChange}
                placeholder="Any special testing requirements, rush order, or notes..."
                rows={4}
              />
            </div>

            <div className="restrictions-note">
              <h4>⚠️ Important Restrictions</h4>
              <ul>
                <li>All samples must be clearly labeled with matching lot numbers</li>
              </ul>
            </div>
          </section>
        )}

        {/* Step 4: Review & Pay */}
        {currentStep === 'review' && (
          <section className="form-section">
            <h2>Review Your Order</h2>
            
            <div className="review-section">
              <h3>Contact Information</h3>
              <p><strong>Company:</strong> {formData.companyName}</p>
              <p><strong>Contact:</strong> {formData.contactName}</p>
              <p><strong>Email:</strong> {formData.email}</p>
              {formData.phone && <p><strong>Phone:</strong> {formData.phone}</p>}
            </div>

            <div className="review-section">
              <h3>Shipping Address</h3>
              <p>{shippingData.shippingAddress}</p>
              <p>{shippingData.city}, {shippingData.state} {shippingData.zipCode}</p>
              <p>{shippingData.country}</p>
            </div>

            <div className="review-section">
              <h3>Samples & Tests</h3>
              {samples.map((sample, index) => (
                <div key={index} className="review-sample">
                  <div className="review-sample-header">
                    <strong>{sample.productName}</strong>
                    <span>Lot: {sample.lotNumber} • {sample.quantity} vials</span>
                  </div>
                  <ul className="options-list">
                    <li>Purity & ID - $120</li>
                    {sample.netContent && <li>Net Content - $20</li>}
                    {sample.endotoxins && <li>Endotoxins - $180</li>}
                    {sample.sterility && <li>Sterility - $100</li>}
                    {sample.conformity && <li>Conformity - $20/vial ({sample.quantity} vials)</li>}
                    {sample.heavyMetals && <li>Heavy Metals - $120</li>}
                    {sample.fentanyl && <li>Fentanyl - $20</li>}
                    {sample.publishToPublic && <li>Public DB - $70/vial ({sample.quantity} vials)</li>}
                  </ul>
                </div>
              ))}
            </div>

            <div className="price-breakdown">
              {samples.map((sample, index) => {
                let sampleTotal = 120 // Purity & ID
                if (sample.netContent) sampleTotal += 20
                if (sample.endotoxins) sampleTotal += 180
                if (sample.sterility) sampleTotal += 100
                if (sample.conformity) sampleTotal += 20 * parseInt(sample.quantity || '1')
                if (sample.heavyMetals) sampleTotal += 120
                if (sample.fentanyl) sampleTotal += 20
                if (sample.publishToPublic) sampleTotal += 70 * parseInt(sample.quantity || '1')
                
                return (
                  <div key={index} className="price-sample-section">
                    <div className="price-sample-name">{sample.productName || `Sample ${index + 1}`} ({sample.quantity} vials)</div>
                    <div className="price-row">
                      <span>Purity & ID</span>
                      <span>$120.00</span>
                    </div>
                    {sample.netContent && (
                      <div className="price-row">
                        <span>Net Content</span>
                        <span>$20.00</span>
                      </div>
                    )}
                    {sample.endotoxins && (
                      <div className="price-row">
                        <span>Endotoxins</span>
                        <span>$180.00</span>
                      </div>
                    )}
                    {sample.sterility && (
                      <div className="price-row">
                        <span>Sterility</span>
                        <span>$100.00</span>
                      </div>
                    )}
                    {sample.conformity && (
                      <div className="price-row">
                        <span>Conformity ({sample.quantity} vials)</span>
                        <span>${20 * parseInt(sample.quantity || '1')}.00</span>
                      </div>
                    )}
                    {sample.heavyMetals && (
                      <div className="price-row">
                        <span>Heavy Metals</span>
                        <span>$120.00</span>
                      </div>
                    )}
                    {sample.fentanyl && (
                      <div className="price-row">
                        <span>Fentanyl</span>
                        <span>$20.00</span>
                      </div>
                    )}
                    {sample.publishToPublic && (
                      <div className="price-row">
                        <span>Public DB ({sample.quantity} vials)</span>
                        <span>${70 * parseInt(sample.quantity || '1')}.00</span>
                      </div>
                    )}
                    <div className="price-sample-total">
                      <span>Sample Total</span>
                      <span>${sampleTotal}.00</span>
                    </div>
                  </div>
                )
              })}
              <div className="price-total">
                <span>Total</span>
                <span>${calculatePrice().toLocaleString()}.00</span>
              </div>
            </div>

            <div className="payment-note">
              <p>After submitting, an invoice and instructions for sending your samples will be sent to your email address.</p>
            </div>

            <div className="turnaround-info">
              <span className="turnaround-icon">⚡</span>
              <span><strong>Turnaround:</strong> 5-10 days</span>
            </div>
          </section>
        )}

        {submitError && (
          <div className="form-error" role="alert">
            {submitError}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="form-navigation">
          {currentStepIndex > 0 && (
            <button type="button" className="btn btn-secondary" onClick={prevStep}>
              ← Previous
            </button>
          )}
          {currentStep !== 'review' ? (
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={nextStep}
              disabled={!canProceed()}
            >
              Continue →
            </button>
          ) : (
            <button type="submit" className="btn btn-primary btn-large" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : `Submit Order - $${calculatePrice().toLocaleString()}.00`}
            </button>
          )}
        </div>

        <p className="form-disclaimer">
          * Required fields. Once submitted, an invoice and sample shipping instructions will be emailed to you.
        </p>
      </form>
    </div>
  )
}

export default RequestCOAPage